// weather.service.ts
import { inject, Service, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, shareReplay, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

import { DailyData, HourlyCodes, YearData, ProcessedDailyWeather, WeatherLocation } from '@interfaces/weather.interface';
import { CUSTOM_WEATHER_LOCATIONS, DEFAULT_WEATHER_LOCATION } from '@data/weather/config';

@Service()
export class WeatherService {
	private API = 'https://archive-api.open-meteo.com/v1/archive';
	private yearCache = new Map<number, Observable<YearData>>();
	private customCache = new Map<string, Observable<YearData>>();
	private http = inject(HttpClient);

	getWeatherForDate(dateStr: string): Observable<ProcessedDailyWeather | null> {
		const customConfig = CUSTOM_WEATHER_LOCATIONS.find(
			(loc) => dateStr >= loc.startDate && dateStr <= loc.endDate
		);

		if (customConfig) {
			return this.fetchCustomWeather(customConfig.location, customConfig.startDate, customConfig.endDate).pipe(
				map(data => this.extractDailyData(data, dateStr))
			);
		}

		// Default fallback
		const year = new Date(dateStr).getFullYear();
		return this.getYearData(year).pipe(
			map(data => this.extractDailyData(data, dateStr))
		);
	}

	private extractDailyData(data: YearData, dateStr: string): ProcessedDailyWeather | null {
		const idx = data.daily.time.indexOf(dateStr);
		if (idx === -1) return null;
		return {
			tMax: data.daily.temperature_2m_max[idx],
			tMin: data.daily.temperature_2m_min[idx],
			dominantCode: this.getDominantCode(data.hourly, dateStr),
			precipitationSum: data.daily.precipitation_sum[idx]
		};
	}

	prefetchYear(year: number): Observable<YearData[]> {
		const fetches: Observable<YearData>[] = [this.getYearData(year)];

		const yearStr = year.toString();
		const yearConfigs = CUSTOM_WEATHER_LOCATIONS.filter(loc => 
			loc.startDate.startsWith(yearStr) || loc.endDate.startsWith(yearStr)
		);

		for (const config of yearConfigs) {
			fetches.push(this.fetchCustomWeather(config.location, config.startDate, config.endDate));
		}

		return forkJoin(fetches);
	}

	/** returns one big blob for the entire year */
	getYearData(year: number): Observable<YearData> {
		if (!this.yearCache.has(year)) {
			const start = `${year}-01-01`;

			// if we're fetching THIS year, cap at today
			const today = new Date().toISOString().split('T')[0];           // e.g. "2025-05-16"
			const end = year === new Date().getFullYear()
				? today
				: `${year}-12-31`;

			const obs = this.fetchWeatherData(DEFAULT_WEATHER_LOCATION, start, end);
			this.yearCache.set(year, obs);
		}
		return this.yearCache.get(year)!;
	}

	private fetchCustomWeather(loc: WeatherLocation, start: string, end: string): Observable<YearData> {
		const cacheKey = `${loc.latitude}_${loc.longitude}_${start}_${end}`;
		if (!this.customCache.has(cacheKey)) {
			const obs = this.fetchWeatherData(loc, start, end);
			this.customCache.set(cacheKey, obs);
		}
		return this.customCache.get(cacheKey)!;
	}

	private fetchWeatherData(loc: WeatherLocation, start: string, end: string): Observable<YearData> {
		const params = new HttpParams()
			.set('latitude', loc.latitude.toString())
			.set('longitude', loc.longitude.toString())
			.set('start_date', start)
			.set('end_date', end)
			.set('daily', 'precipitation_sum,temperature_2m_max,temperature_2m_min')
			.set('hourly', 'weathercode')
			.set('timezone', loc.timezone);

		return this.http
			.get<YearData>(this.API, { params })
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));
	}

	/** picks the most frequent code in a day */
	private getDominantCode(codes: HourlyCodes, date: string): number | null {
		// grab only this day’s codes
		const dayCodes = codes.time
			.map((t, i) => t.startsWith(date) ? codes.weathercode[i] : null)
			.filter((c): c is number => c !== null);

		if (dayCodes.length === 0) {
			return null;   // <— no data for this date
		}

		// build frequency map
		const freq = dayCodes.reduce((acc, c) => {
			acc[c] = (acc[c] || 0) + 1;
			return acc;
		}, {} as Record<number, number>);

		const topCode = +Object.entries(freq)
			.sort(([, a], [, b]) => b - a)[0][0];

		return topCode;
	}

	constructor() { }
}
